import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const I18N = {
  it: { q: "Com'è andata la telefonata con il nostro ufficio?", tap: 'Tocca una stella per valutare', thanks: 'Grazie per il tuo feedback!', comment: 'Vuoi aggiungere qualcosa? (facoltativo)', send: 'Invia commento', sent: 'Commento inviato, grazie!', google: '⭐ Ti va di lasciarci una recensione su Google?', googleBtn: 'Lascia una recensione', invalid: 'Link non valido o scaduto.', low: 'Ci dispiace! Un nostro responsabile ti ricontatterà al più presto.' },
  en: { q: 'How was your call with our office?', tap: 'Tap a star to rate', thanks: 'Thank you for your feedback!', comment: 'Anything to add? (optional)', send: 'Send comment', sent: 'Comment sent, thank you!', google: '⭐ Would you leave us a Google review?', googleBtn: 'Leave a review', invalid: 'Invalid or expired link.', low: 'We are sorry! A manager will contact you shortly.' },
  fr: { q: "Comment s'est passé votre appel avec notre bureau ?", tap: 'Touchez une étoile pour noter', thanks: 'Merci pour votre retour !', comment: 'Un commentaire ? (facultatif)', send: 'Envoyer', sent: 'Commentaire envoyé, merci !', google: '⭐ Voulez-vous nous laisser un avis Google ?', googleBtn: 'Laisser un avis', invalid: 'Lien invalide ou expiré.', low: 'Désolés ! Un responsable vous recontactera très vite.' },
  de: { q: 'Wie war Ihr Anruf bei unserem Büro?', tap: 'Tippen Sie zum Bewerten auf einen Stern', thanks: 'Danke für Ihr Feedback!', comment: 'Möchten Sie etwas hinzufügen? (optional)', send: 'Kommentar senden', sent: 'Kommentar gesendet, danke!', google: '⭐ Möchten Sie uns eine Google-Bewertung hinterlassen?', googleBtn: 'Bewertung abgeben', invalid: 'Ungültiger oder abgelaufener Link.', low: 'Es tut uns leid! Ein Verantwortlicher meldet sich in Kürze.' },
  es: { q: '¿Cómo fue tu llamada con nuestra oficina?', tap: 'Toca una estrella para valorar', thanks: '¡Gracias por tu opinión!', comment: '¿Algo que añadir? (opcional)', send: 'Enviar comentario', sent: '¡Comentario enviado, gracias!', google: '⭐ ¿Nos dejas una reseña en Google?', googleBtn: 'Dejar una reseña', invalid: 'Enlace no válido o caducado.', low: '¡Lo sentimos! Un responsable te contactará pronto.' },
  nl: { q: 'Hoe was uw telefoongesprek met ons kantoor?', tap: 'Tik op een ster om te beoordelen', thanks: 'Bedankt voor uw feedback!', comment: 'Wilt u iets toevoegen? (optioneel)', send: 'Verstuur', sent: 'Opmerking verzonden, bedankt!', google: '⭐ Wilt u een Google-review achterlaten?', googleBtn: 'Review schrijven', invalid: 'Ongeldige of verlopen link.', low: 'Het spijt ons! Een manager neemt snel contact op.' },
  pl: { q: 'Jak przebiegła rozmowa z naszym biurem?', tap: 'Dotknij gwiazdki, aby ocenić', thanks: 'Dziękujemy za opinię!', comment: 'Chcesz coś dodać? (opcjonalnie)', send: 'Wyślij komentarz', sent: 'Komentarz wysłany, dziękujemy!', google: '⭐ Zostawisz nam opinię w Google?', googleBtn: 'Zostaw opinię', invalid: 'Link nieprawidłowy lub wygasł.', low: 'Przepraszamy! Wkrótce się z Tobą skontaktujemy.' },
  pt: { q: 'Como foi a sua chamada com o nosso escritório?', tap: 'Toque numa estrela para avaliar', thanks: 'Obrigado pelo seu feedback!', comment: 'Quer acrescentar algo? (opcional)', send: 'Enviar comentário', sent: 'Comentário enviado, obrigado!', google: '⭐ Quer deixar-nos uma avaliação no Google?', googleBtn: 'Deixar avaliação', invalid: 'Link inválido ou expirado.', low: 'Lamentamos! Um responsável irá contactá-lo em breve.' },
};

const QT = {
  it: 'Come è stata gestita la tua richiesta?',
  en: 'How was your request handled?',
  fr: 'Comment votre demande a-t-elle été gérée ?',
  de: 'Wie wurde Ihre Anfrage bearbeitet?',
  es: '¿Cómo se gestionó tu solicitud?',
  nl: 'Hoe is uw verzoek afgehandeld?',
  pl: 'Jak obsłużono Twoje zgłoszenie?',
  pt: 'Como foi tratado o seu pedido?',
};

export default function CallFeedbackLanding() {
  const { token } = useParams();
  const [state, setState] = useState('loading'); // loading | ready | invalid
  const [lang, setLang] = useState('it');
  const [channel, setChannel] = useState('call');
  const [rating, setRating] = useState(null);
  const [hover, setHover] = useState(null);
  const [comment, setComment] = useState('');
  const [commentSent, setCommentSent] = useState(false);
  const [googleUrl, setGoogleUrl] = useState(null);
  const t = I18N[lang] || I18N.it;

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/cs-control/feedback/public/${token}`);
        setLang(r.data.language || 'it');
        setChannel(r.data.channel || 'call');
        if (r.data.rating) setRating(r.data.rating);
        if (r.data.comment) { setComment(r.data.comment); setCommentSent(true); }
        if (r.data.google_review_url) setGoogleUrl(r.data.google_review_url);
        setState('ready');
      } catch (_) {
        setState('invalid');
      }
    })();
  }, [token]);

  const submit = useCallback(async (value, withComment) => {
    try {
      const body = { rating: value };
      if (withComment !== undefined) body.comment = withComment;
      const r = await axios.post(`${API}/cs-control/feedback/public/${token}`, body);
      if (r.data.google_review_url) setGoogleUrl(r.data.google_review_url);
      else if (value < 4) setGoogleUrl(null);
      return true;
    } catch (_) { return false; }
  }, [token]);

  const tapStar = async (value) => {
    setRating(value);
    await submit(value);
  };

  const sendComment = async () => {
    if (await submit(rating, comment)) setCommentSent(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 text-center" data-testid="feedback-landing">
        <p className="text-xs font-bold tracking-widest text-blue-900 uppercase mb-6">Holiday World</p>
        {state === 'loading' && <p className="text-slate-400 py-10">…</p>}
        {state === 'invalid' && <p className="text-slate-500 py-10" data-testid="feedback-invalid">{t.invalid}</p>}
        {state === 'ready' && (
          <>
            <h1 className="text-xl font-bold text-slate-800">{channel === 'ticket' ? (QT[lang] || QT.it) : t.q}</h1>
            {!rating && <p className="text-sm text-slate-400 mt-1">{t.tap}</p>}
            <div className="flex justify-center gap-2 my-6" data-testid="feedback-stars">
              {[1, 2, 3, 4, 5].map(v => (
                <button key={v} onClick={() => tapStar(v)}
                  onMouseEnter={() => setHover(v)} onMouseLeave={() => setHover(null)}
                  data-testid={`feedback-star-${v}`}
                  className="text-5xl leading-none transition-transform hover:scale-110 focus:outline-none"
                  style={{ color: (hover ?? rating) >= v ? '#f59e0b' : '#e2e8f0' }}>
                  ★
                </button>
              ))}
            </div>
            {rating && (
              <div className="space-y-4" data-testid="feedback-after-rating">
                <p className="text-emerald-600 font-semibold">{t.thanks}</p>
                {rating <= 2 && <p className="text-sm text-slate-500">{t.low}</p>}
                {!commentSent ? (
                  <>
                    <textarea value={comment} onChange={e => setComment(e.target.value)}
                      placeholder={t.comment} rows={3} data-testid="feedback-comment"
                      className="w-full border border-slate-200 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    <button onClick={sendComment} disabled={!comment.trim()} data-testid="feedback-send-comment"
                      className="w-full py-2.5 rounded-xl bg-blue-900 text-white font-semibold text-sm disabled:opacity-40">
                      {t.send}
                    </button>
                  </>
                ) : (
                  comment && <p className="text-sm text-slate-400" data-testid="feedback-comment-sent">✓ {t.sent}</p>
                )}
                {rating >= 4 && googleUrl && (
                  <div className="border-t border-slate-100 pt-4">
                    <p className="text-sm text-slate-600 mb-2">{t.google}</p>
                    <a href={googleUrl} target="_blank" rel="noreferrer" data-testid="feedback-google-btn"
                      className="inline-block px-5 py-2.5 rounded-xl bg-amber-400 text-slate-900 font-bold text-sm hover:bg-amber-300 transition-colors">
                      {t.googleBtn}
                    </a>
                  </div>
                )}
              </div>
            )}
          </>
        )}
        <a href="/privacy" data-testid="feedback-privacy-link"
          className="block mt-6 text-center text-xs text-slate-400 hover:text-slate-600 underline">
          Privacy Policy
        </a>
      </div>
    </div>
  );
}
